/**
 * monetization/types — model of the decoupled monetization kit (canon
 * MONETIZATION_QUOTAS) in its Jornada B2B VARIANT.
 *
 * BUSINESS MODEL of Jornada: B2B **per seat** (seat = supervised employee
 * profile). The `free` tier allows evaluating the product with ONE
 * seat; the `team` tier (paid, manually assigned by provider
 * "none" — without gateway) unlocks more seats and team FEATURES:
 * team reports and integration with time-tracker (proposed entries).
 * No ads in any tier (workforce product, not ad-supported).
 *
 * TypeScript mirror of the Rust model (`core-rust/src/models/monetization.rs` +
 * `monetization/resolve.rs`): keep BOTH in sync.
 */

/** Canonical quota keys, common to the entire portfolio. In Jornada:
 * `collections` = SEATS (live employee profiles, the per-seat lever),
 * `items` = proposed time entries (time-tracker bridge). */
export const QUOTA_KEYS = ["storage_bytes", "items", "collections", "orgs", "attachments"] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/**
 * Feature flags per tier (what the B2B subscription "buys" in addition to
 * seats). Stable keys — mirror of Rust constants:
 *  - `team_reports`        → team reports (/team dashboard).
 *  - `time_tracker_bridge` → proposed entries generation (bridge with
 *    time-tracker; server-side endpoint requires it with monetization ON).
 */
export const TIER_FEATURES = ["team_reports", "time_tracker_bridge"] as const;

export type TierFeature = (typeof TIER_FEATURES)[number];

/** Limits for a tier. `null` = unlimited (env: -1). */
export type QuotaLimits = Record<QuotaKey, number | null>;

export interface TierDef {
  /** Stable id ("free", "team", …). */
  id: string;
  /** Display name. */
  label: string;
  /** true ⇒ ad-financed tier (in Jornada: always false). */
  adsEnabled: boolean;
  /** Feature flags unlocked by this tier (`TIER_FEATURES` keys). */
  features: string[];
  limits: QuotaLimits;
}

/** Resolved configuration (code defaults → env → UI overrides). */
export interface MonetizationConfig {
  /** Master switch; false (default) ⇒ the entire layer is inert. */
  enabled: boolean;
  paymentsEnabled: boolean;
  /** Injected PaymentProvider ID ("none" | "stripe" | …). */
  provider: string;
  /** Tier for anyone without a subscription — and for guests (guest-first). */
  defaultTier: string;
  tiers: TierDef[];
}

/** A persisted override (a `monetization_config` row edited in the admin UI). */
export interface MonetizationOverrideRow {
  key: string;
  value: unknown;
}

/** Actual server-side usage, isolated per user (CC-40). */
export type UsageSnapshot = Record<QuotaKey, number>;

/** Quota evaluation for the UI (usage bar vs limit). */
export interface QuotaEvaluation {
  key: QuotaKey;
  used: number;
  /** null = unlimited. */
  limit: number | null;
  /** 0..1 (0 if unlimited). */
  ratio: number;
  /** true from 80% of the limit onward. */
  nearLimit: boolean;
  exceeded: boolean;
}
